import { z } from "zod";

// =============
// Admin List Pagination & Filtering
// =============

/**
 * Standard pagination input for admin list endpoints.
 * Compatible with React Admin's data provider expectations.
 */
export const AdminListInputSchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	perPage: z.coerce.number().int().min(1).max(100).default(25),
	sortField: z.string().optional(),
	sortOrder: z.enum(["ASC", "DESC"]).default("DESC"),
	filter: z.record(z.string(), z.unknown()).optional(),
	includeDeleted: z.coerce.boolean().default(false),
});
export type AdminListInput = z.infer<typeof AdminListInputSchema>;

/**
 * Helper to create admin list output schema with data and total.
 * React Admin expects { data: T[], total: number } for getList responses.
 */
export function adminListOutput<T extends z.ZodTypeAny>(itemSchema: T) {
	return z.object({
		data: z.array(itemSchema),
		total: z.number().int().nonnegative(),
	});
}

/**
 * Standard single-item response wrapper.
 * React Admin expects { data: T } for getOne/create/update responses.
 */
export function adminItemOutput<T extends z.ZodTypeAny>(itemSchema: T) {
	return z.object({
		data: itemSchema,
	});
}

/**
 * Batch get input schema (getMany).
 */
export const AdminGetManyInputSchema = z.object({
	ids: z.array(z.string()),
});
export type AdminGetManyInput = z.infer<typeof AdminGetManyInputSchema>;

/**
 * Batch delete input schema (deleteMany).
 */
export const AdminDeleteManyInputSchema = z.object({
	ids: z.array(z.string()),
});
export type AdminDeleteManyInput = z.infer<typeof AdminDeleteManyInputSchema>;

/**
 * Batch delete output schema.
 */
export const AdminDeleteManyOutputSchema = z.object({
	data: z.array(z.string()), // IDs of deleted items
});
export type AdminDeleteManyOutput = z.infer<typeof AdminDeleteManyOutputSchema>;

/**
 * Single ID input schema for getOne, delete, restore actions.
 */
export const AdminIdInputSchema = z.object({
	id: z.string(),
});
export type AdminIdInput = z.infer<typeof AdminIdInputSchema>;
